import { FC, useMemo } from 'react';

import { AdditionalOption } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';

interface GiftAdditionalOptionsDetailProps {
  options: AdditionalOption<Image>[];
  /** '옵션 선택하러 가기' 클릭 시 해당 옵션 선택 영역으로 스크롤 */
  onSelectOption?: (optionName: string) => void;
}

const sortByOrder = <T extends { order?: number }>(arr: T[]): T[] =>
  [...arr].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/** 옵션 안내(정보 전용) — 선택지의 이미지/이름/설명 표시. 상세 이미지 직전에 배치 */
export const GiftAdditionalOptionsDetail: FC<GiftAdditionalOptionsDetailProps> =
  function GiftAdditionalOptionsDetail({ options, onSelectOption }) {
    const sortedOptions = useMemo(() => sortByOrder(options), [options]);

    // 이미지/설명이 하나라도 있는 옵션만 안내 대상
    const hasContent = sortedOptions.some((opt) =>
      (opt.choices ?? []).some(
        (c) =>
          c.enabled !== false &&
          ((c.descriptionImage && typeof c.descriptionImage === 'object') ||
            c.description),
      ),
    );
    if (!hasContent) return null;

    return (
      <div className="mt-4 border-t pt-4">
        <h3 className="px-4 font-bold text-gray-700">옵션 안내</h3>
        <div className="mt-2 space-y-5 px-4">
          {sortedOptions.map((option) => {
            const choices = sortByOrder(
              (option.choices ?? []).filter((c) => c.enabled !== false),
            );
            if (choices.length === 0) return null;
            return (
              <div key={option.name} data-gift-option-info={option.name} className="scroll-mt-20">
                <div className="mb-2 flex items-center gap-1.5">
                  <p className="text-sm font-semibold text-gray-500">{option.name}</p>
                  {onSelectOption && (
                    <button
                      type="button"
                      onClick={() => onSelectOption(option.name)}
                      className="ml-auto shrink-0 text-xs font-medium text-indigo-600 underline-offset-2 active:underline"
                    >
                      ‹ 옵션 선택하러 가기
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {choices.map((choice) => {
                    const img =
                      choice.descriptionImage &&
                      typeof choice.descriptionImage === 'object'
                        ? (choice.descriptionImage as Image)
                        : null;
                    return (
                      <div key={choice.name} className="flex gap-3">
                        {img && (
                          <img
                            src={fileBucketUrl(img.url, 'w200')}
                            alt={choice.name}
                            className="h-20 w-20 shrink-0 rounded-lg object-cover"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-sm font-bold"
                            style={{ color: choice.colorHex || undefined }}
                          >
                            {choice.name}
                          </p>
                          {choice.description && (
                            <p className="mt-0.5 whitespace-pre-line break-keep text-sm text-gray-600">
                              {choice.description}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
